import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  OverlayDrawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerFooter,
} from "@fluentui/react-drawer";
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import { Dismiss16Regular, Person16Regular } from "@fluentui/react-icons";
import GlobalTableSearch from "../ManageCustomers/GlobalTableSearch";
import SfUserPickerTable from "./SfUserPickerTable";

// Styles (drawer chrome only — table styles live in SfUserPickerTable)

const useStyles = makeStyles({
  topBar: {
    padding: "8px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  topBarLabel: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    lineHeight: "1.3",
  },
  footerActions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    width: "100%",
  },
  assignBtn: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    border: "none",
    ":hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ":active": {
      backgroundColor: tokens.colorBrandBackgroundPressed,
    },
  },

  // ── Bulk-operation progress banner ─────────────────────────────────
  progressBanner: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: tokens.colorBrandBackground2,
    borderBottom: `1px solid ${tokens.colorBrandStroke2}`,
    color: tokens.colorBrandForegroundLink,
  },
  progressBannerTitle: {
    fontSize: "13px",
    fontWeight: 600,
  },
  progressBannerDesc: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground2,
  },
  disabledOverlay: {
    pointerEvents: "none",
    opacity: 0.5,
  },
  // Shown when the selected queues span more than one Salesforce org so the
  // admin knows users from only one org are shown and other orgs will be skipped.
  mixedOrgWarning: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: tokens.colorStatusWarningBackground1,
    borderBottom: `1px solid ${tokens.colorStatusWarningBorder1}`,
    fontSize: "12px",
    color: tokens.colorStatusWarningForeground1,
    flexShrink: 0,
  },
});

/**
 * AssignMissedCallsUserDrawer
 *
 * Overlay drawer for selecting a single Salesforce user to receive missed call task
 * assignments for a queue (or bulk of queues).
 * Table rendering is fully delegated to the shared SfUserPickerTable component.
 *
 * @param {object}   props
 * @param {boolean}  props.open              - Whether the drawer is open
 * @param {function} props.onDismiss         - Called when the drawer should close
 * @param {object[]} props.queues            - Queue rows being assigned
 * @param {function} props.onConfirm         - (user: object, queues: object[]) => void
 * @param {boolean}  props.isGlobalSelectAll - Whether select-all is active
 * @param {number}   props.totalCount        - Total queue count (for label)
 */
export default function AssignMissedCallsUserDrawer({
  open,
  onDismiss,
  queues = [],
  onConfirm,
  isGlobalSelectAll = false,
  totalCount = 0,
  assigning = false,
  initialSelectedUser = null,
  searchTenantId = undefined,
}) {
  const styles = useStyles();

  const [selectedUser, setSelectedUser] = useState(null);
  const [localFilter, setLocalFilter] = useState("");

  // Seed selection when opening; reset when closing.
  useEffect(() => {
    if (open) {
      setSelectedUser(initialSelectedUser ?? null);
      setLocalFilter("");
    } else {
      setSelectedUser(null);
      setLocalFilter("");
    }
  }, [open, initialSelectedUser]);

  // Receives the full user object from SfUserPickerTable
  const handleSelect = useCallback((user) => {
    setSelectedUser(user);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedUser) return;
    onConfirm?.(selectedUser, queues);
  }, [selectedUser, queues, onConfirm]);

  const handleOpenChange = useCallback(
    (_, { open: isOpen }) => {
      if (assigning) return;
      if (!isOpen) onDismiss();
    },
    [onDismiss, assigning],
  );

  // ── Resolve the Salesforce org for the user picker — single source of truth
  // Use the first linked org in the queues prop. Named here so the same value
  // is shared with the picker and can be validated against before submit.
  const resolvedSfOrgId = useMemo(
    () => queues.find((q) => q.linkedSalesforceOrgId)?.linkedSalesforceOrgId ?? null,
    [queues],
  );

  // Detect when the current (non-select-all) selection spans multiple orgs so
  // the UI can warn the admin that only users from the first org are shown and
  // queues linked to other orgs will be skipped by the backend.
  const hasMixedOrgs = useMemo(() => {
    if (isGlobalSelectAll || queues.length <= 1) return false;
    return queues.some(
      (q) => q.linkedSalesforceOrgId && q.linkedSalesforceOrgId !== resolvedSfOrgId,
    );
  }, [queues, isGlobalSelectAll, resolvedSfOrgId]);

  // Count of queues that will actually be assigned — i.e. those linked to the
  // resolved org. Queues linked to other orgs are skipped by the backend and
  // must not be included in the "Assigning to" count.
  const assignableCount = useMemo(() => {
    if (isGlobalSelectAll || !resolvedSfOrgId) return queues.length;
    return queues.filter((q) => q.linkedSalesforceOrgId === resolvedSfOrgId).length;
  }, [queues, isGlobalSelectAll, resolvedSfOrgId]);

  const queueLabel = isGlobalSelectAll
    ? `${totalCount} queues`
    : queues.length === 1
      ? (queues[0]?.queueName ?? "this queue")
      : hasMixedOrgs
        ? `${assignableCount} queue${assignableCount === 1 ? "" : "s"}`
        : `${queues.length} queues`;

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={handleOpenChange}
      position="end"
      size="medium"
    >
      {/* Header */}
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close drawer"
              icon={<Dismiss16Regular />}
              onClick={onDismiss}
              disabled={assigning}
            />
          }
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <Person16Regular />
            Assign Missed Call Owner
          </span>
        </DrawerHeaderTitle>
      </DrawerHeader>

      {/* Body */}
      <DrawerBody
        style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        {/* Bulk-operation progress banner — shown while assign is in flight */}
        {assigning && (
          <div className={styles.progressBanner} role="status" aria-live="polite">
            <Spinner size="tiny" />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span className={styles.progressBannerTitle}>
                Assigning missed call owner...
              </span>
              <span className={styles.progressBannerDesc}>
                {isGlobalSelectAll
                  ? "Bulk operation in progress. This may take a moment for large tenants."
                  : `Applying to ${queues.length} queue${queues.length === 1 ? "" : "s"}. Please don't close this window.`}
              </span>
            </div>
          </div>
        )}

        {/* Mixed-org warning — shown when the selection includes queues from more
             than one Salesforce org. Users are only shown for the first org;
             the backend will skip queues linked to other orgs. */}
        {hasMixedOrgs && (
          <div className={styles.mixedOrgWarning} role="alert">
            <span>
              <strong>Mixed orgs detected.</strong>{" "}
              The selected queues are linked to multiple Salesforce orgs. Only users
              from <strong>{resolvedSfOrgId}</strong> are shown below. Queues linked
              to other orgs will be skipped during assignment.
            </span>
          </div>
        )}

        <div
          className={assigning ? styles.disabledOverlay : undefined}
          style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
        >
        {/* Top bar: context label + search */}
        <div className={styles.topBar}>
          <Text className={styles.topBarLabel}>
            Assigning to:{" "}
            <strong style={{ color: tokens.colorNeutralForeground1 }}>
              {queueLabel}
            </strong>
            <span style={{ marginLeft: 8, opacity: 0.7 }}>
              · Select one Salesforce user below
            </span>
          </Text>
          <GlobalTableSearch
            value={localFilter}
            onChange={setLocalFilter}
            placeholder="Search by name, username, or Email..."
            debounceMs={0}
          />
        </div>

        {/* Shared user picker table */}
        <SfUserPickerTable
          sfOrgId={resolvedSfOrgId}
          selectedUserId={selectedUser?.sfUserId ?? null}
          onSelect={handleSelect}
          filter={localFilter}
          searchTenantId={searchTenantId}
          pinnedUserId={initialSelectedUser?.sfUserId ?? null}
        />
        </div>
      </DrawerBody>

      {/* Footer */}
      <DrawerFooter>
        <div className={styles.footerActions}>
          <Button appearance="secondary" onClick={onDismiss} disabled={assigning}>
            Cancel
          </Button>
          <Tooltip
            content="Select a user first"
            relationship="label"
            positioning="above"
            visible={!selectedUser ? undefined : false}
          >
            <span>
              <Button
                appearance="primary"
                className={selectedUser ? styles.assignBtn : undefined}
                disabled={!selectedUser || assigning}
                icon={assigning ? <Spinner size="tiny" /> : undefined}
                onClick={handleConfirm}
              >
                {assigning ? "Assigning..." : "Assign Owner"}
              </Button>
            </span>
          </Tooltip>
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
}
